/**
 * 报名记录（服务台入口 `/registration/list`）
 * -----------------------------------------------------------------------------
 * 接真实端点 `GET /api/h5/registrations`（我的报名单列表，仅本人）。
 *
 * 版面口径（设计稿补画本页时定型）：
 *   单号 + **两个并列状态胶囊**（报名链路 / 开课状态，C-22 独立维度）
 *   → 一行「类型 · 批次」→ 尾部「应缴金额 + 动作」。
 * 动作文案与点击落点保持一致（草稿「继续填写」/ 驳回「重新提交材料」/
 * 待缴费「去缴费」/ 其余「查看详情」），避免点进去才发现去向。
 */
import type { RegistrationStatus } from '@enroll/shared';
import { useNavigate } from 'react-router-dom';
import { useMyRegistrations } from '../api/hooks';
import { formatCentsText } from '../components/FormRenderer/logic';
import { ChevronRightIcon, RecordsIcon } from '../components/icons';
import {
  CourseStatusTag,
  EmptyHint,
  ErrorBlock,
  LoadingBlock,
  PageShell,
  StatusTag,
} from '../components/layout';

/** 列表卡尾部的动作：文案跟着落点走，驳回态用红色表示需要用户动手 */
function actionOf(status: RegistrationStatus): { label: string; danger: boolean } {
  switch (status) {
    case 'DRAFT':
      return { label: '继续填写', danger: false };
    case 'REJECTED':
      return { label: '重新提交材料', danger: true };
    case 'PENDING_PAYMENT':
      return { label: '去缴费', danger: false };
    default:
      return { label: '查看详情', danger: false };
  }
}

/** 单号点击后的落点：草稿 / 驳回回表单，待缴费去缴费指引，其余看详情 */
function routeOf(status: RegistrationStatus, id: string): string {
  if (status === 'DRAFT' || status === 'REJECTED') return `/enroll/${id}`;
  if (status === 'PENDING_PAYMENT') return `/payment/${id}`;
  return `/registration/${id}`;
}

export default function RegistrationList() {
  const navigate = useNavigate();
  const mine = useMyRegistrations();

  if (mine.isPending) {
    return (
      <PageShell title="报名记录" back>
        <LoadingBlock text="正在加载报名记录" />
      </PageShell>
    );
  }

  // 网络失败 ≠ 没有报名记录：失败渲染成空态会让学员以为白报了（评审 H-15）
  if (mine.isError) {
    return (
      <PageShell title="报名记录" back>
        <ErrorBlock message="报名记录加载失败，请检查网络后重试" onRetry={() => mine.refetch()} />
      </PageShell>
    );
  }

  const items = mine.data?.items ?? [];

  return (
    <PageShell title="报名记录" back solidNav>
      {items.length === 0 ? (
        <EmptyHint
          icon={<RecordsIcon size={34} strokeWidth={1.5} />}
          title="还没有报名记录"
          description="从服务台首页的「报名办理」开始填写"
          actionText="去报名办理"
          onAction={() => navigate('/enroll/new')}
          fill
        />
      ) : (
        <>
          <div className="section-label">我的报名单（{mine.data?.total ?? items.length}）</div>
          {items.map((item) => {
            const act = actionOf(item.status);
            return (
              <button
                key={item.id}
                type="button"
                className="record-card"
                onClick={() => navigate(routeOf(item.status, item.id))}
              >
                <span className="record-card__head">
                  <span className="record-card__no">{item.registrationNo}</span>
                  {/* 两个**并列**维度：报名+缴费链路状态 / 开课状态（C-22）。
                      结课不改写报名单状态，所以两个都露出来。 */}
                  <span className="pill-row">
                    <StatusTag status={item.status} />
                    <CourseStatusTag status={item.courseStatus} />
                  </span>
                </span>
                <span className="record-card__meta">
                  {item.enrollTypeName} · {item.batchName}
                </span>
                <span className="record-card__foot">
                  <span className="record-card__amount">
                    {item.payableCents > 0 ? (
                      <>
                        <span className="record-card__amount-label">应缴</span>
                        <span className="record-card__amount-value">
                          {formatCentsText(item.payableCents)}
                        </span>
                      </>
                    ) : null}
                  </span>
                  <span
                    className={`record-card__more${act.danger ? ' record-card__more--danger' : ''}`}
                  >
                    {act.label}
                    <ChevronRightIcon size={12} />
                  </span>
                </span>
              </button>
            );
          })}
        </>
      )}
    </PageShell>
  );
}
